/* eslint-disable array-callback-return */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import APP_CONST from "../../../shared/constants";
import Card from "../Card/Card";
import Chart from "../Chart/Chart";
import Loader from "../../shared/Loader/Loader";
import "./OverviewDashboard.css";
import PlaceholderComponent from "../../../containers/ArchiveDashboard/PlaceholderComponent";

import * as templateActions from "../../../../store/actions/templateActions";
import DropdownFilters from "../../shared/DropdownFIlters/DropdownFilters";
import * as documentActions from "../../../../store/actions/documentActions";
import * as zoneActions from "../../../../store/actions/zoneActions";
import _ from "underscore";

const OverviewDashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { metricsData, initialOverviewPageLoad, orgGuid, apiInitConfigObj } = useSelector(
    (store) => store.zone
  );
  const { lockStatus, isLockLoading } = useSelector((store) => store.template);

  const emtpyMetricsOption1 = () => {
    if (
      metricsData.artifact - metricsData.artifactmissingdocuments === 0 &&
      metricsData.artifactmissingdocuments === 0
    ) {
      return {
        data: [98, 0],
        color: ["#cccccc", "#73bce6"],
      };
    } else {
      return {
        data: [
          metricsData.artifact - metricsData.artifactmissingdocuments,
          metricsData.artifactmissingdocuments,
        ],
        color: ["#0086d2", "#73bce6"],
      };
    }
  };

  const PIECHART_DATA1 = {
    colors: emtpyMetricsOption1().color,
    series: [
      {
        innerSize: "68%",
        data: emtpyMetricsOption1().data,
      },
    ],
  };

  const emtpyMetrics = () => {
    if (
      metricsData.finalized === 0 &&
      metricsData.awaitingreview === 0 &&
      metricsData.flaggedbyqc === 0
    ) {
      return {
        data: [99, 0],
        color: ["#cccccc", "#73bce6"],
      };
    } else {
      return {
        data: [
          metricsData.finalized,
          metricsData.awaitingreview,
          metricsData.flaggedbyqc,
        ],
        color: ["#00ac26", "#767676", "#f86800"],
      };
    }
  };

  const PIECHART_DATA2 = {
    colors: emtpyMetrics().color,
    series: [
      {
        innerSize: "68%",
        data: emtpyMetrics().data,
      },
    ],
  };

  const pieChartConfig1 = {
    ...APP_CONST.PIE_CHART_1,
    ...PIECHART_DATA1,
  };
  const pieChartConfig2 = {
    ...APP_CONST.PIE_CHART_2,
    ...PIECHART_DATA2,
  };
  const artifactsChartComponent = [
    {
      className: "chart-document",
      spanClass: "box blue-box",
      chartLabel: t("OVERVIEW.CONTAINING_DOCUMENTS"),
      paraLabel: metricsData.doccontainspercent + "%",
    },
    {
      className: "chart-missing",
      spanClass: "",
      chartLabel: t("OVERVIEW.MISSING_REQUIRED_DOCUMENTS"),
      paraLabel: metricsData.artifactmissingdocuments,
    },
  ];

  const documentChartComponent = [
    {
      className: "flagged-document",
      spanClass: "box orange-box",
      chartLabel: t("TMF.TEXT_FLAGGEDBYQC"),
      paraLabel: metricsData.flaggedbyqc,
    },
    {
      className: "awaiting-review",
      spanClass: "box grey-box",
      chartLabel: t("TMF.TEXT_AWAITING"),
      paraLabel: metricsData.awaitingreview,
    },
    {
      className: "finalized-review",
      spanClass: "box green-box",
      chartLabel: t("TMF.TEXT_FINALIZED"),
      paraLabel: metricsData.finalized,
    },
  ];
  
  useEffect(()=>{
        let relVer = (apiInitConfigObj.releaseversion !== undefined) ? `?i=`+apiInitConfigObj.releaseversion : "";
        navigate("/" + relVer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[apiInitConfigObj.releaseversion])

  useEffect(() => {
    if (!_.isEmpty(orgGuid)) {
      dispatch(templateActions.getAllTemplates(orgGuid));
    }
    dispatch(templateActions.getLockStatus());
    dispatch(documentActions.fetchAndLoadDocMaster());
    dispatch(templateActions.getDropZoneSettings());
    dispatch(templateActions.getCountry());
    dispatch(templateActions.getSite());
    dispatch(templateActions.getMilestoneDropDownData());
    dispatch(templateActions.getMilestoneGroup());
    dispatch(documentActions.getCustomizedColumState());
    dispatch(zoneActions.fetchMetricsData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderHeaderComponent = () => {
    return (
      <div className="overview-content pl-30 pr-30">
        <h3>{t("OVERVIEW.TEXT_OVERVIEW")}</h3>
        <DropdownFilters />
      </div>
    );
  };

  /* Represents a arifacts chart.
   * passing pie chart data through piechartconfig1
   */
  const renderArtifactsChart = () => {
    return (
      <Card className="statcard ml-30">
        <div className="chart-header">{t("OVERVIEW.TEXT_ARTIFACTS")}</div>
        <p className="chart-dynamic-value">{metricsData.artifact}</p>
        <Chart options={pieChartConfig1} />
        <div className="chart-content">
          {artifactsChartComponent.map((chartItem) => (
            <div className={chartItem.className} key={chartItem.className}>
              <p className="label-document">
                <span className={chartItem.spanClass}></span>
                {chartItem.chartLabel}
              </p>
              <p className="document-value">{chartItem.paraLabel}</p>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  /* Represents a document chart.
   * passing pie chart data through piechartconfig2
   */
  const renderDocumentChart = () => {
    return (
      <Card className="statcard documentsCard mr-30">
        <div className="chart-header">{t("OVERVIEW.TEXT_RECORDS")}</div>
        <p className="chart-dynamic-value1">{metricsData.totaldoccount}</p>
        <Chart options={pieChartConfig2} />
        <div className="chart-content chart-content-document">
          {documentChartComponent.map((chartItem) => (
            <div className={chartItem.className} key={chartItem.chartLabel}>
              <p className="label-document">
                <span className={chartItem.spanClass}></span>
                {chartItem.chartLabel}
              </p>
              <p className="document-value">{chartItem.paraLabel}</p>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  return isLockLoading ? (
    <div className="overview-page-loader">
      <Loader />
    </div>
  ) : lockStatus !== APP_CONST.ADMIN_TEMPLATE_STATUS[1] ? (
    <PlaceholderComponent title={t("TMF.TEXT_STUDYNOTINSTANTIATED")} />
  ) : initialOverviewPageLoad ? (
    <div className="overview-page-loader">
      <Loader />
    </div>
  ) : (
    <div className="overview-dashboard">
      {renderHeaderComponent()}
      <div className="charts">
        {renderArtifactsChart()}
        {renderDocumentChart()}
      </div>
    </div>
  );
};

export default OverviewDashboard;
